import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { Link } from 'react-router-dom';
import { products, categories, siteImages } from '../data/mock';
import { ArrowRight, ArrowUpRight, Cpu, Zap, Truck, ShieldCheck, Headphones } from 'lucide-react';

const HomePage = () => {
  const featuredProducts = products.slice(0, 4);
  const bestSellers = products.filter(p => p.badge === 'Bestseller').slice(0, 4);

  useEffect(() => {
    document.title = 'Rad Tech Foundry | Latest Electronics, Smart Gadgets & Tech Innovations';
  }, []);

  return (
    <div className="min-h-screen bg-white text-[#1a1a1a]" data-testid="home-page">
      <Header />
      <CartDrawer />

      <main className="pt-[104px]">
        {/* ================= HERO ================= */}
        <section className="relative bg-[#f1f5f9] overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white border border-athletic-200 rounded-full mb-8">
                  <Zap className="w-3.5 h-3.5 text-athletic-500" />
                  <span className="text-athletic-700 text-[11px] font-medium tracking-[0.18em] uppercase">Free USA Shipping · Latest Tech</span>
                </div>

                <h1 className="text-5xl sm:text-6xl lg:text-7xl leading-[1.02] mb-8 text-[#1a1a1a]"
                    style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, letterSpacing: '-0.02em' }}>
                  Tech<br />
                  <span className="italic text-athletic-500">innovations</span><br />
                  await.
                </h1>

                <p className="text-base text-[#1a1a1a]/65 leading-relaxed mb-10 max-w-md">
                  Stay ahead with the latest electronics and smart gadgets—high-performance audio, gaming gear and smart-home tech, expertly curated for you.
                </p>

                <div className="flex flex-wrap gap-4 mb-12">
                  <Link
                    to="/shop"
                    data-testid="hero-shop-btn"
                    className="group inline-flex items-center gap-3 px-8 py-4 bg-athletic-500 hover:bg-athletic-600 text-white font-medium tracking-wide rounded-full transition-all"
                  >
                    Shop Electronics
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <Link
                    to="/why-choose-us"
                    className="group inline-flex items-center gap-3 px-8 py-4 bg-transparent border border-[#1a1a1a]/20 hover:border-[#1a1a1a] text-[#1a1a1a] font-medium tracking-wide rounded-full transition-all"
                  >
                    Our Story
                    <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </div>

                <div className="flex gap-10 pt-8 border-t border-[#1a1a1a]/10">
                  {[
                    { value: '24+', label: 'Tech Products' },
                    { value: '$75', label: 'Free Shipping' },
                    { value: '30d', label: 'Easy Returns' },
                  ].map((stat, i) => (
                    <div key={i}>
                      <p className="text-3xl text-athletic-500" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>{stat.value}</p>
                      <p className="text-[10px] text-[#1a1a1a]/50 uppercase tracking-[0.18em] mt-1">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative">
                <div className="aspect-[4/5] rounded-3xl overflow-hidden bg-white">
                  <img
                    src={siteImages.heroMain}
                    alt="Rad Tech Foundry Electronics"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-white shadow-lg rounded-2xl p-5 border border-gray-100 max-w-[220px]">
                  <div className="flex items-center gap-2 mb-2">
                    <Cpu className="w-4 h-4 text-athletic-500" />
                    <span className="text-[10px] uppercase tracking-[0.18em] text-[#1a1a1a]/60 font-medium">Drop of the Week</span>
                  </div>
                  <p className="text-sm text-[#1a1a1a] leading-snug">Wireless headphones + ANC earbuds for sound on the go.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ================= TRUST STRIP ================= */}
        <section className="bg-white border-y border-gray-100">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { icon: Cpu, text: 'Latest Technology' },
                { icon: Headphones, text: 'High-Performance Gadgets' },
                { icon: Truck, text: 'Free US Shipping $75+' },
                { icon: ShieldCheck, text: '30-Day Returns' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 justify-center md:justify-start">
                  <item.icon className="w-5 h-5 text-athletic-500" strokeWidth={1.5} />
                  <span className="text-[#1a1a1a]/70 text-sm tracking-wide">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= CATEGORIES ================= */}
        <section className="py-24 bg-white" data-testid="home-categories">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-14">
              <span className="text-athletic-500 text-[11px] font-medium uppercase tracking-[0.25em]">Shop By Category</span>
              <h2 className="text-4xl lg:text-5xl mt-3 text-[#1a1a1a]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                Explore the <span className="italic text-athletic-500">tech</span>
              </h2>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
              {categories.map((category) => (
                <Link
                  key={category.id}
                  to={`/shop?category=${category.slug}`}
                  data-testid={`home-cat-${category.slug}`}
                  className="group relative aspect-[4/5] rounded-3xl overflow-hidden bg-[#f1f5f9]"
                >
                  <img
                    src={category.image}
                    alt={category.name}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/0 to-transparent" />
                  <div className="absolute inset-0 flex flex-col justify-end p-6">
                    <h3 className="text-2xl text-white" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                      {category.name}
                    </h3>
                    <div className="flex items-center gap-2 mt-2 text-white/80 group-hover:text-athletic-300 transition-colors">
                      <span className="text-xs uppercase tracking-wider">Shop now</span>
                      <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ================= BESTSELLERS ================= */}
        <section className="py-24 bg-[#f1f5f9]" data-testid="home-bestsellers">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-12 gap-6">
              <div>
                <span className="text-athletic-500 text-[11px] font-medium uppercase tracking-[0.25em]">Most loved</span>
                <h2 className="text-4xl lg:text-5xl mt-3 text-[#1a1a1a]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                  Bestsellers
                </h2>
              </div>
              <Link
                to="/shop"
                className="group inline-flex items-center gap-2 text-[#1a1a1a]/60 hover:text-athletic-500 font-medium transition-colors text-sm tracking-wide"
              >
                Shop all
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {(bestSellers.length ? bestSellers : featuredProducts).map((product) => (
                <Link
                  key={product.id}
                  to={`/product/${product.slug}`}
                  data-testid={`home-product-${product.slug}`}
                  className="group block"
                >
                  <div className="aspect-square rounded-2xl bg-white overflow-hidden mb-4 relative border border-gray-100">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    {product.badge && (
                      <span className="absolute top-3 left-3 px-3 py-1 bg-white/95 text-[#1a1a1a] text-[10px] font-medium tracking-[0.12em] uppercase rounded-full">
                        {product.badge}
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-athletic-500 uppercase tracking-[0.18em] font-medium mb-1">
                    {categories.find(c => c.id === product.categoryId)?.name}
                  </p>
                  <h3 className="text-base text-[#1a1a1a] mb-2 group-hover:text-athletic-500 transition-colors line-clamp-2 min-h-[44px]">
                    {product.name}
                  </h3>
                  <p className="text-base text-[#1a1a1a]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
                    ${product.price.toFixed(2)}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ================= TECH PROMISE ================= */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="relative order-2 lg:order-1">
                <div className="aspect-[4/5] rounded-3xl overflow-hidden bg-[#f1f5f9]">
                  <img
                    src={siteImages.powerBackground}
                    alt="High-performance electronics"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -right-6 bg-athletic-500 text-white p-6 rounded-2xl max-w-[200px]">
                  <p className="text-4xl" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>100%</p>
                  <p className="text-xs uppercase tracking-[0.15em] mt-1 opacity-90">Curated & Quality-Checked</p>
                </div>
              </div>

              <div className="order-1 lg:order-2">
                <span className="text-athletic-500 text-[11px] font-medium uppercase tracking-[0.25em]">Future-Proof Your Life</span>
                <h2 className="text-4xl lg:text-5xl mt-3 mb-8 text-[#1a1a1a]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                  Innovative gadgets,<br />
                  <span className="italic text-athletic-500">real performance.</span>
                </h2>
                <p className="text-[#1a1a1a]/65 leading-relaxed mb-10">
                  Every Rad Tech Foundry product is hand-picked for the latest technology, high performance and user-friendly design. No filler—just expertly curated electronics at competitive prices, backed by free USA shipping.
                </p>

                <div className="grid grid-cols-2 gap-6">
                  {[
                    { title: 'Performance', desc: 'High-performance gadgets engineered for speed.' },
                    { title: 'Innovation', desc: 'The newest tech to keep you ahead of the curve.' },
                    { title: 'Design', desc: 'Sleek, user-friendly designs for any setup.' },
                    { title: 'Value', desc: 'Competitive pricing on tech you can trust.' },
                  ].map((item, i) => (
                    <div key={i} className="border-t border-[#1a1a1a]/10 pt-4">
                      <h3 className="text-xl text-[#1a1a1a] mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                        {item.title}
                      </h3>
                      <p className="text-sm text-[#1a1a1a]/60 leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ================= STEPS ================= */}
        <section className="py-24 bg-[#f1f5f9]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-athletic-500 text-[11px] font-medium uppercase tracking-[0.25em]">How It Works</span>
              <h2 className="text-4xl lg:text-5xl mt-3 text-[#1a1a1a]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                Gear up in <span className="italic text-athletic-500">four steps</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-4 gap-8">
              {[
                { num: '01', title: 'Discover', desc: 'Explore the latest electronics and smart gadgets.' },
                { num: '02', title: 'Build Your Setup', desc: 'Pick the audio, gaming and smart-home gear you love.' },
                { num: '03', title: 'Checkout', desc: 'Secure, encrypted payment in just a few taps.' },
                { num: '04', title: 'Power Up', desc: 'Fast free shipping—unbox and plug in.' },
              ].map((step, i) => (
                <div key={i} className="text-center">
                  <p className="text-5xl text-athletic-500 mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}>
                    {step.num}
                  </p>
                  <h3 className="text-2xl text-[#1a1a1a] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                    {step.title}
                  </h3>
                  <p className="text-sm text-[#1a1a1a]/60 leading-relaxed max-w-xs mx-auto">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= CTA ================= */}
        <section className="relative py-28 bg-white">
          <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-5xl lg:text-6xl text-[#1a1a1a] mb-6 leading-tight"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
              Ready to <span className="italic text-athletic-500">upgrade?</span>
            </h2>
            <p className="text-base text-[#1a1a1a]/65 mb-10 max-w-lg mx-auto leading-relaxed">
              Discover the latest electronics and smart gadgets today—your next favorite device is just a click away.
            </p>
            <Link
              to="/shop"
              data-testid="cta-shop-btn"
              className="inline-flex items-center gap-3 px-10 py-4 bg-athletic-500 hover:bg-athletic-600 text-white font-medium tracking-wide rounded-full transition-all"
            >
              Shop Electronics
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HomePage;
